import { formatCurrency, ObligationStatus } from "@sp-crm/core";
import type { AppRouter } from "@sp-crm/server/rpc";
import { useMutation, useQuery } from "@tanstack/react-query";
import type { inferRouterOutputs } from "@trpc/server";
import { PlacementPage } from "components/feature";
import { PageHeader } from "components/layout";
import { trpc } from "components/rpc/trpc";
import { DetailPageLayout } from "components/shared/detail-page-layout";
import { CrmTable } from "components/table/crm-table";
import { CrmTableColumn } from "components/table/types";
import { Badge } from "components/ui/badge";
import { ErrorMessage } from "components/ui/error-message";
import { PrimaryCompactButton } from "components/ui/primary-button";
import { Spinner } from "components/ui/spinner";
import { Toggle } from "components/ui/toggle";
import React, { useCallback, useState } from "react";

type ObligationRow = inferRouterOutputs<AppRouter>["obligations"]["list"][number];

const statusBadgeType = (
    status: ObligationRow["status"],
): "error" | "info" | "highlight" | "success" => {
    switch (status) {
        case ObligationStatus.Sent:
            return "highlight";
        case ObligationStatus.Received:
            return "success";
        default:
            return "info";
    }
};

const CrossRegionPaymentsTable: React.FC = () => {
    const trpcClient = trpc.useTRPC();
    const [showArchived, setShowArchived] = useState(false);
    const obligationsQuery = useQuery(
        trpcClient.obligations.list.queryOptions({
            view: showArchived ? "all" : "active",
        }),
    );

    const markSentMutation = useMutation({
        ...trpcClient.obligations.markSent.mutationOptions(),
        onSuccess: () => obligationsQuery.refetch(),
    });
    const markReceivedMutation = useMutation({
        ...trpcClient.obligations.markReceived.mutationOptions(),
        onSuccess: () => obligationsQuery.refetch(),
    });

    const isMutating = markSentMutation.isPending || markReceivedMutation.isPending;

    const markSent = useCallback(
        (obligationId: string) => markSentMutation.mutate({ obligationId }),
        [markSentMutation],
    );
    const markReceived = useCallback(
        (obligationId: string) => markReceivedMutation.mutate({ obligationId }),
        [markReceivedMutation],
    );

    const columns: CrmTableColumn<ObligationRow>[] = React.useMemo(
        () => [
            {
                key: "debtorTerritoryName",
                header: "Payment",
                renderCell: ({ record }) =>
                    record ? (
                        <div className="text-sm text-gray-900">
                            {record.debtorTerritoryName ?? "Unknown"}
                            <span className="text-gray-400"> → </span>
                            {record.creditorTerritoryName ?? "Unknown"}
                        </div>
                    ) : (
                        <span />
                    ),
            },
            {
                key: "type",
                header: "Type",
                renderCell: ({ record }) =>
                    record ? (
                        <div className="text-sm text-gray-500">{record.type}</div>
                    ) : (
                        <span />
                    ),
            },
            {
                key: "amountCents",
                header: "Amount",
                renderCell: ({ record }) =>
                    record ? (
                        <div className="text-sm text-gray-900">
                            {formatCurrency((record.amountCents ?? 0) / 100)}
                        </div>
                    ) : (
                        <span />
                    ),
            },
            {
                key: "status",
                header: "Status",
                renderCell: ({ record }) =>
                    record ? (
                        <div className="flex items-center gap-2">
                            <Badge
                                type={statusBadgeType(record.status)}
                                value={record.status ?? ""}
                            />
                            {record.isArchived ? (
                                <Badge type="info" value="Archived" />
                            ) : null}
                        </div>
                    ) : (
                        <span />
                    ),
            },
            {
                key: "id",
                header: "Action",
                renderCell: ({ record }) => {
                    if (!record) {
                        return <span />;
                    }
                    if (record.canMarkSent) {
                        return (
                            <PrimaryCompactButton
                                disabled={isMutating}
                                onClick={() => record.id && markSent(record.id)}>
                                Mark sent
                            </PrimaryCompactButton>
                        );
                    }
                    if (record.canMarkReceived) {
                        return (
                            <PrimaryCompactButton
                                disabled={isMutating}
                                onClick={() => record.id && markReceived(record.id)}>
                                Mark received
                            </PrimaryCompactButton>
                        );
                    }
                    return <span className="text-sm text-gray-400">—</span>;
                },
            },
        ],
        [isMutating, markSent, markReceived],
    );

    if (obligationsQuery.isLoading) {
        return (
            <div className="bg-white rounded border border-gray-200 p-4">
                <Spinner />
            </div>
        );
    }

    if (obligationsQuery.isError) {
        return (
            <div className="bg-white rounded border border-gray-200 p-4">
                <ErrorMessage component="CrossRegionPayments">
                    {obligationsQuery.error.message}
                </ErrorMessage>
            </div>
        );
    }

    const actionError = markSentMutation.error ?? markReceivedMutation.error;

    return (
        <div className="space-y-2">
            {actionError ? (
                <ErrorMessage component="CrossRegionPayments">
                    {actionError.message}
                </ErrorMessage>
            ) : null}
            <div className="flex justify-end">
                <Toggle
                    label="Show archived"
                    checked={showArchived}
                    onChange={setShowArchived}
                />
            </div>
            <div className="bg-white rounded border border-gray-200">
                <CrmTable data={obligationsQuery.data ?? []} columns={columns} />
            </div>
        </div>
    );
};

export const CrossRegionPayments: React.FC<unknown> = () => {
    return (
        <PlacementPage>
            <div>
                <PageHeader title="Cross-Region Payments" />
                <DetailPageLayout>
                    <div className="pt-2">
                        <CrossRegionPaymentsTable />
                    </div>
                </DetailPageLayout>
            </div>
        </PlacementPage>
    );
};
